import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';

import * as UserPreferencesActions from './user-preferences.actions';
import * as UserPreferencesSelectors from './user-preferences.selectors';
import { Observable } from 'rxjs';
import { UserPreferencesState } from './user-preferences.models';
import { Currencies, PackDescriptionType } from '@cpak/enums';
import { AttributeTypeIds } from '@cpak/adp-cpak-products-service-models';
import { DEFAULT_LANG, DEFAULT_TIMEZONE, TIMEZONES_CONFIG } from '@cpak/consts';
import { TimeZone } from '@cpak/interfaces';
import { PlantsEnum } from '@cpak/adp-cpak-quotes-service-models';
import { map } from 'rxjs/operators';

@Injectable()
export class UserPreferencesFacade {
  constructor(private readonly store: Store) {}

  public setLanguage(language: string): void {
    this.store.dispatch(UserPreferencesActions.setLanguage({ language }));
  }

  public setTimeZone(timeZone: string): void {
    this.store.dispatch(UserPreferencesActions.setTimeZone({ timeZone }));
  }

  public setCurrency(currency: Currencies): void {
    this.store.dispatch(UserPreferencesActions.setCurrency({ currency }));
  }

  public setPartNumberDisplay(partNumberDisplay: AttributeTypeIds): void {
    this.store.dispatch(UserPreferencesActions.setPartNumberDisplay({ partNumberDisplay }));
  }

  public setPackDescriptionDisplay(packDescriptionDisplay: PackDescriptionType): void {
    this.store.dispatch(UserPreferencesActions.setPackDescriptionDisplay({ packDescriptionDisplay }));
  }

  public setShowInternalNotes(showInternalNotes: boolean): void {
    this.store.dispatch(UserPreferencesActions.setShowInternalNotes({ showInternalNotes }));
  }

  public setShowSpecialInstructions(showSpecialInstructions: boolean): void {
    this.store.dispatch(UserPreferencesActions.setShowSpecialInstructions({ showSpecialInstructions }));
  }

  public setReceiveEmailNotifications(receiveEmailNotifications: boolean): void {
    this.store.dispatch(UserPreferencesActions.setReceiveEmailNotifications({ receiveEmailNotifications }));
  }

  public setUserDefaultTimeZone(): void {
    const timeZone = this.getUserDefaultTimeZone(TIMEZONES_CONFIG);
    this.setTimeZone(timeZone);
  }

  public setUserDefaultLanguage(availableLangs: string[]): void {
    const language = this.getUserDefaultLanguage(availableLangs);
    this.setLanguage(language);
  }

  public setDefaultConfig(availableLangs: string[], plant: PlantsEnum): void {
    this.store.dispatch(
      UserPreferencesActions.setDefaultConfig({
        language: this.getUserDefaultLanguage(availableLangs),
        timeZone: this.getUserDefaultTimeZone(TIMEZONES_CONFIG),
        packDescriptionDisplay: plant === PlantsEnum.houston ? PackDescriptionType.COMMON : PackDescriptionType.CUSTOMER,
      })
    );
  }

  public setPlant(plant: PlantsEnum): void {
    this.store.dispatch(UserPreferencesActions.setPlant({ plant }));
  }

  public getLanguage(): Observable<string> {
    return this.store.pipe(select(UserPreferencesSelectors.getLanguage()));
  }

  public getTimeZone(): Observable<string> {
    return this.store.pipe(select(UserPreferencesSelectors.getTimeZone()));
  }

  public getCurrency(): Observable<Currencies> {
    return this.store.pipe(select(UserPreferencesSelectors.getCurrency()));
  }

  public getPartNumberDisplay(): Observable<AttributeTypeIds> {
    return this.store.pipe(select(UserPreferencesSelectors.getPartNumberDisplay()));
  }

  public getPackDescriptionDisplay(): Observable<PackDescriptionType | null> {
    return this.store.pipe(select(UserPreferencesSelectors.getPackDescriptionDisplay()));
  }

  public getPartNumberLabelDisplay(): Observable<string> {
    const productAttributeToLabelKey = new Map<AttributeTypeIds, string>([
      [AttributeTypeIds.CatalogNumber, 'catalogNumber'],
      [AttributeTypeIds.GlobalNumber, 'globalNumber'],
      [AttributeTypeIds.ManufacturingNumber, 'manufacturingNumber'],
    ]);
    return this.store.pipe(
      select(UserPreferencesSelectors.getPartNumberDisplay()),
      map(partNumber => productAttributeToLabelKey.get(partNumber) || partNumber)
    );
  }

  public getShowInternalNotes(): Observable<boolean> {
    return this.store.pipe(select(UserPreferencesSelectors.getShowInternalNotes()));
  }

  public getShowSpecialInstructions(): Observable<boolean> {
    return this.store.pipe(select(UserPreferencesSelectors.getShowSpecialInstructions()));
  }

  public getReceiveEmailNotifications(): Observable<boolean> {
    return this.store.pipe(select(UserPreferencesSelectors.getReceiveEmailNotifications()));
  }

  public getAllUserPreferences(): Observable<UserPreferencesState> {
    return this.store.pipe(select(UserPreferencesSelectors.getAllUserPreferences()));
  }

  public getPlant(): Observable<PlantsEnum | undefined> {
    return this.store.pipe(select(UserPreferencesSelectors.getPlant()));
  }

  private getUserDefaultLanguage(availableLangs: string[] = []): string {
    for (let browserLang of navigator.languages) {
      let language = availableLangs.find(lang => lang === browserLang);
      if (language) return browserLang;

      const browserLangPref = browserLang.split('-')[0];

      language = availableLangs.find(lang => lang.split('-')[0] === browserLangPref);

      if (language) return language;
    }
    return DEFAULT_LANG;
  }

  private getUserDefaultTimeZone(timeZones: TimeZone[]): string {
    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (timeZones.some(timeZone => timeZone.timeZoneName === browserTimeZone)) {
      return browserTimeZone;
    }

    return DEFAULT_TIMEZONE;
  }
}
